import {
  filterFromQuery,
  filterGames,
  filterToQuery,
  genresFor,
  getCatalogue,
  INPUT_LABEL,
  KIND_LABEL,
  label,
  type Filter,
  type Input,
  type Kind,
} from "../catalogue.ts";
import { gameGrid } from "../components.ts";
import { clear, h } from "../dom.ts";
import { setTitle } from "../router.ts";

/** Kind → genre → facets, plus search. The filter lives in the query string, so results can be linked. */
export async function browsePage(_p: unknown, query: URLSearchParams): Promise<HTMLElement> {
  const cat = await getCatalogue();
  let f: Filter = filterFromQuery(query);
  setTitle(f.kind ? KIND_LABEL[f.kind] : "Browse");

  const results = h("div", { class: "results" });
  const count = h("p", { class: "dim count", "aria-live": "polite" });
  const genreRow = h("div", { class: "filter-row", role: "group", "aria-label": "Genre" });
  const kindRow = h("div", { class: "filter-row tabs", role: "group", "aria-label": "Kind" });

  const update = (next: Partial<Filter>, push = false) => {
    f = { ...f, ...next };
    const qs = filterToQuery(f);
    if (location.search !== qs) history[push ? "pushState" : "replaceState"](null, "", `/browse${qs}`);
    draw();
  };

  const pill = (text: string, on: boolean, onclick: () => void) =>
    h("button", { type: "button", class: `pill${on ? " on" : ""}`, "aria-pressed": on ? "true" : "false", onclick }, text);

  const search = h("input", {
    type: "search",
    id: "q",
    placeholder: "Search games, creators, genres",
    value: f.q ?? "",
    autocomplete: "off",
    oninput: (e: Event) => update({ q: (e.target as HTMLInputElement).value }),
  });

  const facetRow = h("fieldset", { class: "filter-row facets" }, h("legend", { class: "sr-only" }, "Facets"));

  const draw = () => {
    clear(
      kindRow,
      pill("All", !f.kind, () => update({ kind: null, genre: null }, true)),
      cat.taxonomy.kinds
        .filter((k) => cat.games.some((g) => g.kind === k))
        .map((k: Kind) => pill(KIND_LABEL[k], f.kind === k, () => update({ kind: k, genre: null }, true))),
    );
    const genres = genresFor(cat, f.kind);
    clear(
      genreRow,
      genres.length > 1 ? pill("Any genre", !f.genre, () => update({ genre: null }, true)) : null,
      genres.length > 1 ? genres.map((g) => pill(label(g), f.genre === g, () => update({ genre: f.genre === g ? null : g }, true))) : null,
    );
    const check = (text: string, on: boolean, toggle: (v: boolean) => void) =>
      h("label", { class: "check" }, h("input", { type: "checkbox", checked: on, onchange: (e: Event) => toggle((e.target as HTMLInputElement).checked) }), text);
    clear(
      facetRow,
      h("legend", { class: "sr-only" }, "Facets"),
      check("Multiplayer", !!f.multiplayer, (v) => update({ multiplayer: v })),
      (Object.keys(INPUT_LABEL) as Input[]).map((i) =>
        check(INPUT_LABEL[i], !!f.input?.includes(i), (v) => update({ input: v ? [...(f.input ?? []), i] : (f.input ?? []).filter((x) => x !== i) })),
      ),
      check("Straight into play", !!f.noPrePlay, (v) => update({ noPrePlay: v })),
    );
    const list = filterGames(cat.games, f);
    count.textContent = `${list.length} ${list.length === 1 ? "result" : "results"}`;
    clear(results, gameGrid(list, "No matches. Try fewer filters or another word."));
  };
  draw();

  return h(
    "div",
    { class: "browse" },
    h("h1", null, "Browse"),
    h("form", { class: "search", role: "search", onsubmit: (e: Event) => e.preventDefault() }, h("label", { for: "q", class: "sr-only" }, "Search"), search),
    kindRow,
    genreRow,
    facetRow,
    count,
    results,
  );
}
